import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { AssociationsAndSuppliers } from '@/components/AssociationsAndSuppliers'
import { Helmet } from 'react-helmet-async'

export default function About() {
  // Handle client-side navigation
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault()
    window.history.pushState({}, '', href)
    window.dispatchEvent(new Event('popstate'))
    window.scrollTo(0, 0)
  }

  // Newly Added Schema Data for the About Page
  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "AboutPage",
        "headline": "About Busy Beaver Construction",
        "description": "Serving Calgary since 1999, Busy Beaver Construction is the leader in custom decks, fences, and outdoor living spaces for residential and commercial properties.",
        "url": "https://busybeaverconstruction.com/about",
        "mainEntity": {
          "@id": "https://busybeaverconstruction.com/#organization"
        }
      },
      {
        "@type": "GeneralContractor",
        "@id": "https://busybeaverconstruction.com/#organization",
        "name": "Busy Beaver Construction",
        "url": "https://busybeaverconstruction.com/",
        "logo": "https://busybeaverconstruction.com/assets/images/2020/03/busy-beaver-logo.png",
        "image": "https://busybeaverconstruction.com/assets/images/Decks/Composite Decks/timbertech-composite-stock.jpg",
        "description": "Busy Beaver Construction is a full-service outdoor construction company specializing in Custom Decks, Fences, and outdoor Living Spaces in Calgary since 1999.",
        "telephone": "+1-403-286-5400",
        "foundingDate": "1999",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Calgary",
          "addressRegion": "AB",
          "addressCountry": "CA"
        },
        "areaServed": {
          "@type": "City",
          "name": "Calgary"
        }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://busybeaverconstruction.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "About",
            "item": "https://busybeaverconstruction.com/about"
          }
        ]
      }
    ]
  };

  return (
    <main>
      <Helmet>
        <title>About Busy Beaver Construction | Calgary's Premier Deck & Fence Builder</title>
        <meta name="description" content="Busy Beaver Construction is Calgary's longest operating deck and fence builder since 1999. Family-owned, specializing in custom decks, fences and outdoor living spaces." />
        <meta name="keywords" content="Busy Beaver Construction, Calgary deck builder, deck company Calgary, fence contractor Calgary, custom outdoor living spaces, BBB A+ rated deck builder" />
        <link rel="canonical" href="https://busybeaverconstruction.com/about" />
        <meta property="og:title" content="About Busy Beaver Construction | Calgary's Premier Deck Builder" />
        <meta property="og:description" content="Serving Calgary since 1999, Busy Beaver Construction is the leader in custom decks, fences, and outdoor living spaces for residential and commercial properties." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://busybeaverconstruction.com/about" />
        <meta property="og:image" content="https://busybeaverconstruction.com/assets/images/Decks/Composite Decks/timbertech-composite-stock.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="preload" href="/assets/images/Decks/Composite Decks/timbertech-composite-stock.jpg" as="image" />
      </Helmet>

      {/* JSON-LD Schema placed OUTSIDE Helmet to ensure rendering */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />

      <Header />

      {/* Hero Banner */}
      <section className="relative h-[400px] flex items-center justify-center">
        <img 
          src="/assets/images/Decks/Composite Decks/timbertech-composite-stock.jpg"
          alt="Busy Beaver Construction TimberTech composite deck installation in Calgary"
          className="absolute inset-0 w-full h-full object-cover loading-eager"
          fetchPriority="high"
        />        
        <div className="relative z-10 mt-64 text-center">
          <div className="inline-block bg-busy-green rounded py-3 mx-4 px-4 lg:px-8">
            <p className="text-white text-base font-medium rounded">The Longest Operating Deck Builder in Calgary</p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container">
          <h1 className="text-center text-5xl lg:text-6xl font-primary ">About Busy Beaver Construction</h1>
          <p className="text-center text-xl text-gray-500 mb-8 md:mb-14">It's all in the name.</p>
          
          <div className=" mx-auto space-y-6 text-gray-500 text-base leading-relaxed">
            <p>
              <strong className="font-semibold">Busy Beaver Construction</strong> is a full-service, outdoor construction company that specializes in Custom Decks, Fences, and outdoor Living Spaces. Since 1999, Busy Beaver has been serving the Calgary area delivering beautiful, customized decks and fences. Whether you are looking for a simple deck, or a complete backyard makeover, we offer customized solutions that address each client's budget, taste, and preferences.
            </p>
            
            <p>
              What started as a small, one crew operation, has now grown to be Calgary's leading and longest running deck and fence builder for residential and commercial properties. With more than 28 years of experience, this family-owned operation continuously strives to build a name and reputation as the leader in quality custom decks and fences.
            </p>
            
            <p>
              Our team is selected carefully to ensure the highest of standards, professionalism and comprehensive knowledge in the outdoor construction industry. We ensure our team is receiving frequent training and certification to keep up with industry advancements. Our installation crews approach each project with a foundation of strong design aesthetics, and commitment to quality craftsmanship.
            </p>
            
            <p>
              Busy Beaver is a proud A+ member of the Better Business Bureau. We take pride in building beautiful outdoor spaces that exceed our client's expectations. We love what we do and are confident you will enjoy working with us from the design process till the project is complete.
            </p>
            
            <p>
              Please check out our <a href="/services" onClick={(e) => handleNavigation(e, '/services')} className="text-beaver-red hover:underline">project gallery</a>, read through our <a href="/testimonials" onClick={(e) => handleNavigation(e, '/testimonials')} className="text-beaver-red hover:underline">spectacular reviews</a>, and when you are ready to start your project <a href="/appointment" onClick={(e) => handleNavigation(e, '/appointment')} className="text-beaver-red hover:underline">we are here to help</a>.
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-gray-50 border-t border-gray-100">
        <div className="container mx-auto px-4 max-w-7xl">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-primary text-center text-gray-900 mb-4">Our Services</h2>
          <p className="text-center text-lg text-gray-500 font-accent mb-12 uppercase tracking-wide">Expert craftsmanship for your outdoor space</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Custom Decks */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col">
              <div className="h-48 overflow-hidden">
                <img 
                  src="/assets/images/Decks/Composite Decks/timbertech-composite-stock.jpg" 
                  alt="Custom Decks" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" 
                  loading="lazy" 
                />
              </div>
              <div className="p-6 flex flex-col flex-grow text-center">
                <h3 className="text-xl font-secondary font-bold mb-3 text-gray-900">Decks</h3>
                <p className="text-gray-600 mb-6 font-text text-sm leading-relaxed flex-grow">
                  Premium composite, cedar, and pressure-treated wood decks engineered to withstand Calgary's climate.
                </p>
                <a 
                  href="/services/decks" 
                  onClick={(e) => handleNavigation(e, '/services/decks')} 
                  className="text-beaver-green font-bold hover:underline font-accent uppercase tracking-wide inline-flex justify-center items-center text-sm"
                >
                  Explore Decks <i className="fas fa-arrow-right ml-2 text-xs"></i>
                </a>
              </div>
            </div>

            {/* Pergolas & Privacy Walls */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col">
              <div className="h-48 overflow-hidden">
                <img 
                  src="/assets/images/Privacy Walls/Custom-Cedar-Privacy-wall-with-Stained-Glass-inserts.jpg" 
                  alt="Pergolas & Privacy Walls" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" 
                  loading="lazy" 
                />
              </div>
              <div className="p-6 flex flex-col flex-grow text-center">
                <h3 className="text-xl font-secondary font-bold mb-3 text-gray-900">Pergolas & Privacy Walls</h3>
                <p className="text-gray-600 mb-6 font-text text-sm leading-relaxed flex-grow">
                  Custom structures designed to provide shade, seclusion, and architectural beauty to your yard.
                </p>
                <a 
                  href="/services/pergolas-and-privacy-walls" 
                  onClick={(e) => handleNavigation(e, '/services/pergolas-and-privacy-walls')} 
                  className="text-beaver-green font-bold hover:underline font-accent uppercase tracking-wide inline-flex justify-center items-center text-sm"
                >
                  Explore Options <i className="fas fa-arrow-right ml-2 text-xs"></i>
                </a>
              </div>
            </div>

            {/* Gazebo */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col">
              <div className="h-48 overflow-hidden">
                <img 
                  src="/assets/images/Pergola/Cedar-Gazebo-with-shingled-roof.jpg" 
                  alt="Gazebos" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" 
                  loading="lazy" 
                />
              </div>
              <div className="p-6 flex flex-col flex-grow text-center">
                <h3 className="text-xl font-secondary font-bold mb-3 text-gray-900">Gazebos</h3>
                <p className="text-gray-600 mb-6 font-text text-sm leading-relaxed flex-grow">
                  Elegant, fully covered outdoor structures perfect for dining, hot tubs, and year-round shelter.
                </p>
                <a 
                  href="/services/gazebo" 
                  onClick={(e) => handleNavigation(e, '/services/gazebo')} 
                  className="text-beaver-green font-bold hover:underline font-accent uppercase tracking-wide inline-flex justify-center items-center text-sm"
                >
                  Explore Gazebos <i className="fas fa-arrow-right ml-2 text-xs"></i>
                </a>
              </div>
            </div>

            {/* Living Spaces */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col">
              <div className="h-48 overflow-hidden">
                <img 
                  src="/assets/images/Other/Living Spaces/Outdoor-covered-Living-Space-on-Mahogany-lake.jpeg" 
                  alt="Outdoor Living Spaces" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" 
                  loading="lazy" 
                />
              </div>
              <div className="p-6 flex flex-col flex-grow text-center">
                <h3 className="text-xl font-secondary font-bold mb-3 text-gray-900">Living Spaces</h3>
                <p className="text-gray-600 mb-6 font-text text-sm leading-relaxed flex-grow">
                  Complete backyard transformations including outdoor kitchens and multi-function entertainment areas.
                </p>
                <a 
                  href="/services/living-spaces" 
                  onClick={(e) => handleNavigation(e, '/services/living-spaces')} 
                  className="text-beaver-green font-bold hover:underline font-accent uppercase tracking-wide inline-flex justify-center items-center text-sm"
                >
                  Explore Living Spaces <i className="fas fa-arrow-right ml-2 text-xs"></i>
                </a>
              </div>
            </div>

            {/* Railings */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col">
              <div className="h-48 overflow-hidden">
                <img 
                  src="/assets/images/Decks/Composite Decks/Timbertech-Terrain-multi-level-deck-in-Brown-Oak-trim-and-Silver-Maple-surface.-LED-strip-lighting-on-stairs.jpg" 
                  alt="Deck Railings" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" 
                  loading="lazy" 
                />
              </div>
              <div className="p-6 flex flex-col flex-grow text-center">
                <h3 className="text-xl font-secondary font-bold mb-3 text-gray-900">Railings</h3>
                <p className="text-gray-600 mb-6 font-text text-sm leading-relaxed flex-grow">
                  Safe, code-compliant aluminum, glass, and composite railing systems to complement your deck.
                </p>
                <a 
                  href="/services/railings" 
                  onClick={(e) => handleNavigation(e, '/services/railings')} 
                  className="text-beaver-green font-bold hover:underline font-accent uppercase tracking-wide inline-flex justify-center items-center text-sm"
                >
                  Explore Railings <i className="fas fa-arrow-right ml-2 text-xs"></i>
                </a>
              </div>
            </div>

            {/* Fences */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col">
              <div className="h-48 overflow-hidden">
                <img 
                  src="/assets/images/Fence/Cedar-Custom-Horizontal-fence-1x6-_-1x2-pickets.jpg" 
                  alt="Custom Fences" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" 
                  loading="lazy" 
                />
              </div>
              <div className="p-6 flex flex-col flex-grow text-center">
                <h3 className="text-xl font-secondary font-bold mb-3 text-gray-900">Fences</h3>
                <p className="text-gray-600 mb-6 font-text text-sm leading-relaxed flex-grow">
                  Durable property fencing in cedar, pressure-treated wood, and maintenance-free materials.
                </p>
                <a 
                  href="/services/fences" 
                  onClick={(e) => handleNavigation(e, '/services/fences')} 
                  className="text-beaver-green font-bold hover:underline font-accent uppercase tracking-wide inline-flex justify-center items-center text-sm"
                >
                  Explore Fences <i className="fas fa-arrow-right ml-2 text-xs"></i>
                </a>
              </div>
            </div>

            {/* Landscaping */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col md:col-span-2 lg:col-span-3 lg:w-1/3 lg:mx-auto">
              <div className="h-48 overflow-hidden">
                <img 
                  src="/assets/images/Other/Living Spaces/Multi-function-Outdoor-Living-space-with-BBQ-deck-and-lower-seating-and-dining-area.jpg" 
                  alt="Landscaping Services" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" 
                  loading="lazy" 
                />
              </div>
              <div className="p-6 flex flex-col flex-grow text-center">
                <h3 className="text-xl font-secondary font-bold mb-3 text-gray-900">Landscaping</h3>
                <p className="text-gray-600 mb-6 font-text text-sm leading-relaxed flex-grow">
                  Professional landscaping solutions to integrate your new structure perfectly with your yard.
                </p>
                <a 
                  href="/services/landscaping" 
                  onClick={(e) => handleNavigation(e, '/services/landscaping')} 
                  className="text-beaver-green font-bold hover:underline font-accent uppercase tracking-wide inline-flex justify-center items-center text-sm"
                >
                  Explore Landscaping <i className="fas fa-arrow-right ml-2 text-xs"></i>
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Client Testimonials */}
      <section className="py-24 relative lg:mb-10">
        <div className="absolute inset-0">
          <img 
            src="/assets/images/Other/Living Spaces/Outdoor-covered-Living-Space-on-Mahogany-lake.jpeg"
            alt="Custom outdoor covered living space by Busy Beaver Construction on Mahogany lake in Calgary"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-black/70"></div>
        
        <div className="container relative z-10">
          <h2 className="text-center text-5xl lg:text-6xl font-script text-white mb-12">Clients on Busy Beaver Construction</h2>
          
          <div className="max-w-3xl mx-auto">
            <blockquote className="text-white text-lg italic leading-relaxed mb-6">
              "Francois and Christine were both a real pleasure to work with from quote to completing the project. We absolutely love our deck. Each phase of the project was executed with professionalism and everyone was super friendly. They used quality materials and our deck is a solid structure. It looks modern and we are proud to show it off. Busy Beaver is the best."
            </blockquote>
            <div className="text-right text-white">
              <p className="font-bold">Joe Shenfield</p>
            </div>
          </div>
        </div>
      </section>

      {/* Associations and Suppliers Section - now using the component */}
      <AssociationsAndSuppliers />

      <Footer />
    </main>
  )
}